import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorProvider from "@core-ui/ContextServices/ApiUrlCreator";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import { ArticlePropsStoreProvider } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import Renderer from "@ext/markdown/core/render/components/Renderer";
import { PrintProvider } from "@ext/print/PrintContext";
import type { ArticlePreview } from "@ext/print/types";
import { type ElementType, type ReactNode, useEffect, useRef } from "react";

const ArticleServices = ({
	apiUrlCreator,
	children,
	articleProps,
}: {
	apiUrlCreator: ApiUrlCreator;
	children: ReactNode;
	articleProps: ClientArticleProps;
}) => {
	return (
		<ApiUrlCreatorProvider.Provider value={apiUrlCreator}>
			<ArticlePropsStoreProvider data={articleProps}>
				<ResourceService.Provider>
					<>{children}</>
				</ResourceService.Provider>
			</ArticlePropsStoreProvider>
		</ApiUrlCreatorProvider.Provider>
	);
};

type ArticlePrintPreviewProps = {
	item: ArticlePreview;
	components: Record<string, ElementType>;
	withTitle: boolean;
	onRender?: VoidFunction;
};

export const ArticlePrintPreview = ({ item, components, withTitle, onRender }: ArticlePrintPreviewProps) => {
	const onRenderRef = useRef(onRender);
	onRenderRef.current = onRender;
	useEffect(() => {
		// Pagination reads the committed DOM, not the elements being built during render.
		if (item.content) onRenderRef.current?.();
	}, [item.content]);
	const articleProps = { logicPath: item.logicPath, title: item.title } as ClientArticleProps;
	return (
		<PrintProvider>
			<ArticleServices apiUrlCreator={item.apiUrlCreator} articleProps={articleProps}>
				{withTitle && (
					<h1 data-level={item.level} id={item.logicPath}>
						{item.title}
					</h1>
				)}
				{Renderer(item.content, { components }, true)}
			</ArticleServices>
		</PrintProvider>
	);
};
